import { useQueryClient } from "@tanstack/react-query";
import Select from "components/Elements/Selectv2";
import { TagComponentBase } from "components/common/TagComponentBase";
import React, { FC, useEffect, useMemo, useState } from "react";
import {
  flowRicochetStatusesQuery,
  flowRicochetTagsQuery,
  RicochetStatusOption,
  RicochetTagOption,
} from "pages/FlowBuilderv2/flowBuilderQueries";
import {
  RicochetAction,
  RicochetNodeData,
} from "pages/FlowBuilderv2/Nodes/NodeData";
import { SidePanelComponentProps } from "../FlowBuilderSidePanel";
import { useSidePanelReadOnly } from "../SidePanelReadOnlyContext";

const actionOptions: { key: RicochetAction; label: string }[] = [
  { key: "updateStatus", label: "Update status" },
  { key: "addTag", label: "Add tags" },
  { key: "createTask", label: "Create task" },
  { key: "createNote", label: "Add note" },
  { key: "getTask", label: "Get task" },
  { key: "getTasks", label: "Get all tasks" },
  { key: "getNotes", label: "Get all notes" },
];

const inputClass =
  "w-[260px] px-[12px] py-[5px] rounded-sm border bg-themed-card text-themed-text placeholder:text-themed-textSecondary";

const errorBorder = (showErrors: boolean, invalid: boolean) =>
  showErrors && invalid ? "border-[#F43F5E]" : "border-themed-border";

const getSelectedTags = (nodeData: RicochetNodeData) => {
  const tags =
    nodeData.tagNames?.filter((tag) => !!tag?.trim()) ||
    (nodeData.tagName?.trim() ? [nodeData.tagName.trim()] : []);
  return Array.from(new Set(tags));
};

const RicochetSettings: FC<SidePanelComponentProps<RicochetNodeData>> = ({
  nodeData,
  setNodeData,
  setIsError,
  showErrors,
}) => {
  const readOnly = useSidePanelReadOnly();
  const queryClient = useQueryClient();
  const [statuses, setStatuses] = useState<RicochetStatusOption[]>([]);
  const [tags, setTags] = useState<RicochetTagOption[]>([]);
  const [statusLoadFailed, setStatusLoadFailed] = useState(false);

  const action = nodeData.action || "updateStatus";
  const selectedTags = getSelectedTags(nodeData);
  const statusInvalid = action === "updateStatus" && !nodeData.statusId;
  const tagInvalid = action === "addTag" && selectedTags.length === 0;
  const taskInvalid =
    action === "createTask" && !String(nodeData.taskName ?? "").trim();
  const noteInvalid =
    action === "createNote" && !String(nodeData.note ?? "").trim();
  const taskIdInvalid =
    action === "getTask" && !String(nodeData.taskId ?? "").trim();

  const tagOptions = useMemo(
    () => Array.from(new Set(tags.map((tag) => tag.name).filter(Boolean))),
    [tags]
  );

  useEffect(() => {
    setIsError(
      statusInvalid || tagInvalid || taskInvalid || noteInvalid || taskIdInvalid
    );
  }, [
    setIsError,
    statusInvalid,
    tagInvalid,
    taskInvalid,
    noteInvalid,
    taskIdInvalid,
  ]);

  useEffect(() => {
    (async () => {
      try {
        const data = await queryClient.ensureQueryData(
          flowRicochetStatusesQuery()
        );
        setStatuses(data);
        setStatusLoadFailed(false);
      } catch {
        setStatuses([]);
        setStatusLoadFailed(true);
      }

      try {
        const data = await queryClient.ensureQueryData(flowRicochetTagsQuery());
        setTags(data);
      } catch {
        setTags([]);
      }
    })();
  }, [queryClient]);

  const update = (patch: Partial<RicochetNodeData>) =>
    setNodeData({ ...nodeData, ...patch });

  return (
    <div
      className={`p-5 font-inter text-[14px] font-normal leading-[22px] text-themed-text${
        readOnly ? " pointer-events-none select-none" : ""
      }`}
    >
      <div className="flex flex-col gap-5">
        <div className="flex justify-between items-center gap-[20px]">
          <div>Action</div>
          <div className="w-[260px]">
            <Select
              panelClassName="w-[260px]"
              options={actionOptions.map((option) => ({
                key: option.key,
                title: option.label,
              }))}
              value={action}
              onChange={(nextAction) => update({ action: nextAction })}
              placeholder="Select action"
              noDataPlaceholder="No actions"
            />
          </div>
        </div>

        {action === "updateStatus" && (
          <>
            <div className="flex justify-between items-center gap-[20px]">
              <div>Status</div>
              <div className="w-[260px]">
                <Select
                  panelClassName="w-[260px]"
                  options={statuses.map((status) => ({
                    key: String(status.id),
                    title: `${status.name} (${status.id})`,
                  }))}
                  value={nodeData.statusId || ""}
                  onChange={(statusId) => {
                    const status = statuses.find(
                      (item) => String(item.id) === statusId
                    );
                    update({
                      statusId,
                      statusName: status?.name,
                    });
                  }}
                  placeholder={
                    statusLoadFailed ? "Statuses unavailable" : "Select status"
                  }
                  noDataPlaceholder="No statuses"
                />
              </div>
            </div>

            <div className="flex justify-between items-center gap-[20px]">
              <div>Status ID</div>
              <input
                className={`${inputClass} ${errorBorder(
                  showErrors,
                  statusInvalid
                )}`}
                value={nodeData.statusId || ""}
                onChange={(event) =>
                  update({
                    statusId: event.target.value.trim(),
                    statusName: statuses.find(
                      (status) => String(status.id) === event.target.value
                    )?.name,
                  })
                }
                placeholder="Status ID"
              />
            </div>
          </>
        )}

        {action === "addTag" && (
          <div className="flex justify-between items-start gap-[20px]">
            <div className="pt-[5px]">Tags</div>
            <div className="w-[260px]">
              <TagComponentBase
                tags={selectedTags}
                possibleTags={tagOptions}
                onTagChange={(nextTags) =>
                  update({
                    tagNames: nextTags,
                    tagName: nextTags[0] || "",
                  })
                }
                className={showErrors && tagInvalid ? "!border-[#F43F5E]" : ""}
                inputPlaceholder="Select tags"
                noMatchingMessage="No matching tags"
                serverListEmptyMessage="No tags"
              />
            </div>
          </div>
        )}

        {action === "createTask" && (
          <>
            <div className="flex justify-between items-center gap-[20px]">
              <div>Task title</div>
              <input
                className={`${inputClass} ${errorBorder(
                  showErrors,
                  taskInvalid
                )}`}
                value={nodeData.taskName || ""}
                onChange={(event) => update({ taskName: event.target.value })}
                placeholder="Task title"
              />
            </div>

            <div className="flex justify-between items-start gap-[20px]">
              <div className="pt-[5px]">Description</div>
              <textarea
                className={`${inputClass} min-h-[86px] resize-y`}
                value={nodeData.taskDescription || ""}
                onChange={(event) =>
                  update({ taskDescription: event.target.value })
                }
                placeholder="Description"
              />
            </div>

            <div className="flex justify-between items-center gap-[20px]">
              <div>Priority</div>
              <input
                className={`${inputClass}`}
                type="number"
                min={1}
                max={10}
                value={nodeData.taskPriority || ""}
                onChange={(event) =>
                  update({ taskPriority: event.target.value })
                }
                placeholder="4"
              />
            </div>

            <div className="flex justify-between items-center gap-[20px]">
              <div>Invitees</div>
              <input
                className={`${inputClass}`}
                value={nodeData.taskInvitees || ""}
                onChange={(event) =>
                  update({ taskInvitees: event.target.value })
                }
                placeholder="name@example.com"
              />
            </div>

            <div className="flex justify-between items-center gap-[20px]">
              <div>Reminder date</div>
              <input
                className={`${inputClass}`}
                type="date"
                value={nodeData.taskReminderDate || ""}
                onChange={(event) =>
                  update({ taskReminderDate: event.target.value })
                }
              />
            </div>

            <div className="flex justify-between items-center gap-[20px]">
              <div>Reminder time</div>
              <input
                className={`${inputClass}`}
                type="time"
                value={nodeData.taskReminderTime || ""}
                onChange={(event) =>
                  update({ taskReminderTime: event.target.value })
                }
              />
            </div>

            <div className="flex justify-end gap-5">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!nodeData.taskReminderPopup}
                  onChange={(event) =>
                    update({ taskReminderPopup: event.target.checked })
                  }
                />
                <span>Popup</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!nodeData.taskReminderEmail}
                  onChange={(event) =>
                    update({ taskReminderEmail: event.target.checked })
                  }
                />
                <span>Email</span>
              </label>
            </div>
          </>
        )}

        {action === "createNote" && (
          <div className="flex justify-between items-start gap-[20px]">
            <div className="pt-[5px]">Note</div>
            <textarea
              className={`${inputClass} min-h-[120px] resize-y ${errorBorder(
                showErrors,
                noteInvalid
              )}`}
              value={nodeData.note || ""}
              onChange={(event) => update({ note: event.target.value })}
              placeholder="Note"
            />
          </div>
        )}

        {action === "getTask" && (
          <div className="flex justify-between items-center gap-[20px]">
            <div>Task ID</div>
            <input
              className={`${inputClass} ${errorBorder(
                showErrors,
                taskIdInvalid
              )}`}
              value={nodeData.taskId || ""}
              onChange={(event) => update({ taskId: event.target.value })}
              placeholder="Task ID"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default RicochetSettings;
